import { createContext, useContext, useState, useCallback } from 'react';
import { setSessionToken, getSessionToken, clearSessionToken, apiFetch } from '../fetchInterceptor';
import { getAttributionForConversion } from '../lib/adAttribution';
import { fireConversion } from '../lib/gtagConversions';
import { metaTrack } from '../lib/metaPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext();

// Helper to get auth headers - used by all fetch calls across the app
export const getAuthHeaders = () => {
  const token = getSessionToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Helper to make authenticated fetch calls - uses apiFetch for reliability in iframes
export const authFetch = (url, options = {}) => {
  return apiFetch(url, options);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);

  const storeToken = (token) => {
    if (token) setSessionToken(token);
  };

  const clearToken = () => {
    clearSessionToken();
  };

  const checkAuth = useCallback(async () => {
    setLoading(true);
    // Capture the token at request time to detect if a new login occurred
    const tokenAtRequestTime = getSessionToken();
    try {
      let response = await authFetch(`${API_URL}/api/auth/me`);
      // Transient server/proxy hiccup (5xx, Cloudflare challenge) — retry once
      // before treating the user as logged out. A false negative here dumps
      // freshly-paid signups back on the landing page.
      if (!response.ok && response.status >= 500) {
        await new Promise((r) => setTimeout(r, 1200));
        response = await authFetch(`${API_URL}/api/auth/me`);
      }

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      } else {
        setUser(null);
        // Only clear the token if it hasn't changed since the request was made.
        // This prevents a race condition where an initial checkAuth(no token) returns 401
        // AFTER a successful login has stored a new token — wiping the fresh token.
        const currentToken = getSessionToken();
        if (currentToken && currentToken === tokenAtRequestTime) {
          clearToken();
        }
        return null;
      }
    } catch (error) {
      // Network-level failure (not a 401) — retry once before giving up so a
      // momentary blip right after the Stripe redirect doesn't log users out.
      try {
        await new Promise((r) => setTimeout(r, 1200));
        const retry = await authFetch(`${API_URL}/api/auth/me`);
        if (retry.ok) {
          const userData = await retry.json();
          setUser(userData);
          return userData;
        }
      } catch { /* fall through */ }
      console.error('Auth check failed:', error);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const loginWithGoogle = () => {
    // Branded Google OAuth — uses CountBucks' own Google Cloud project so
    // the consent screen shows the CountBucks logo, not "Emergent". The
    // backend handles the OAuth dance and sets our session_token cookie
    // before redirecting back to /dashboard.
    const nextPath = '/dashboard';
    window.location.href = `${API_URL}/api/auth/google/login?next=${encodeURIComponent(nextPath)}`;
  };

  const login = loginWithGoogle;

  const registerWithEmail = async (email, password, name) => {
    setLoading(true);
    setAuthError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/email/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          name,
          attribution: getAttributionForConversion(),
          // Bind this signup to their anonymous visitor_id so the traffic
          // dashboard can resolve a real name for their future pageviews.
          visitor_id: (() => { try { return localStorage.getItem('cb_vid') || null; } catch { return null; } })(),
        }),
      });

      // Defensively parse the response. If the server returned HTML (e.g.,
      // Cloudflare 520/502 error page during a brief outage), response.json()
      // throws "Unexpected token '<'" which is gibberish to users. Detect this
      // and surface a clear message instead.
      const ct = response.headers.get('content-type') || '';
      let data;
      if (ct.includes('application/json')) {
        data = await response.json();
      } else {
        // Non-JSON response — server is unreachable or returned an error page
        throw new Error(
          response.status >= 500
            ? "Our server hit a snag — please try again in a moment."
            : "We couldn't reach the server. Check your connection and retry."
        );
      }

      if (!response.ok) {
        const msg = typeof data.detail === 'string'
          ? data.detail
          : Array.isArray(data.detail)
            ? data.detail.map(e => e.msg).join(', ')
            : 'Registration failed';
        throw new Error(msg);
      }

      // Account is now created immediately at register time (no OTP gate).
      // Log the user in, then the caller opens Stripe Checkout for the trial.
      if (data.token) storeToken(data.token);
      setUser(data);
      setPendingEmail(null);
      fireConversion('signup', { transactionId: data.user_id || email });
      try {
        const _plan = new URLSearchParams(window.location.search).get('plan') || undefined;
        metaTrack('CompleteRegistration', { planId: _plan });
      } catch { metaTrack('CompleteRegistration'); }
      return { success: true, ...data };
    } catch (error) {
      setAuthError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const verifyRegistration = async (email, code) => {
    setLoading(true);
    setAuthError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/email/verify-registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }
      
      if (data.token) storeToken(data.token);
      setUser(data);
      setPendingEmail(null);
      // Fire Google Ads signup conversion — no-op until a label is
      // configured in lib/gtagConversions.js. Uses the user_id as
      // transaction_id so repeated verifications don't double-count.
      fireConversion('signup', { transactionId: data.user_id || email });
      // Meta CompleteRegistration (browser Pixel + server Conversions API,
      // shared event_id). Pass the plan from the register URL (if any) so the
      // event carries content_name + currency. Token is already stored above.
      try {
        const _plan = new URLSearchParams(window.location.search).get('plan') || undefined;
        metaTrack('CompleteRegistration', { planId: _plan });
      } catch { metaTrack('CompleteRegistration'); }
      return { success: true, ...data };
    } catch (error) {
      setAuthError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const loginWithEmail = async (email, password) => {
    setLoading(true);
    setAuthError(null);
    setRequires2FA(false);
    try {
      const response = await fetch(`${API_URL}/api/auth/email/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      if (data.requires_2fa) {
        setRequires2FA(true);
        setPendingEmail(email);
        return { success: true, requires2FA: true, ...data };
      }
      
      if (data.token) storeToken(data.token);
      setUser(data);
      return { success: true, ...data };
    } catch (error) {
      setAuthError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const verify2FALogin = async (code) => {
    setLoading(true);
    setAuthError(null);
    try {
      const response = await authFetch(`${API_URL}/api/auth/email-2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || '2FA verification failed');
      }
      
      if (data.token) storeToken(data.token);
      await checkAuth();
      setRequires2FA(false);
      setPendingEmail(null);
      return { success: true };
    } catch (error) {
      setAuthError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationCode = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/email/resend-code?email=${encodeURIComponent(email)}`, {
        method: 'POST',
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend code');
      }
      
      return { success: true, ...data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const forgotPassword = async (email) => {
    setLoading(true);
    setAuthError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send reset code');
      }
      
      setPendingEmail(email);
      return { success: true, ...data };
    } catch (error) {
      setAuthError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email, code, newPassword) => {
    setLoading(true);
    setAuthError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }
      
      if (data.token) storeToken(data.token);
      setUser(data);
      setPendingEmail(null);
      return { success: true, ...data };
    } catch (error) {
      setAuthError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authFetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      clearToken();
      setUser(null);
      setRequires2FA(false);
      setPendingEmail(null);
      window.location.href = '/';
    }
  };

  const exchangeSessionId = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: {
          'X-Session-ID': sessionId,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData.token) storeToken(userData.token);
        setUser(userData);
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Session exchange failed:', error);
      return null;
    }
  };

  const clearAuthError = () => setAuthError(null);

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      authError,
      requires2FA,
      pendingEmail,
      checkAuth,
      login,
      loginWithGoogle,
      loginWithEmail,
      registerWithEmail,
      verifyRegistration,
      verify2FALogin,
      resendVerificationCode,
      forgotPassword,
      resetPassword,
      logout,
      exchangeSessionId,
      clearAuthError,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
